import { useMemo, useState } from "react";
import { cn } from "@/core/utils";
import { Button } from "@/core/components/ui/button";
import { Sparkles, X } from "lucide-react";
import { useActionsTrack } from "@/core/services/menu-analytics";
import type { FormBlock, FormField } from "../schemas/blocks";

/**
 * Renderer do block `form` (ST-5 — Figma Operação, abrir Ordem de Serviço).
 *
 * Form seccionado (título + hint + campo), com chips single/multi-select, select, text e
 * textarea. O submit dispara `onSubmit(form_id, values, action_token)`; o botão "Melhorar
 * com IA" de um campo dispara `onAssist(...)` sem submeter (RFC §12.3).
 *
 * Máquina de estado local (RFC §12.2a): idle → submitting. Ao submeter, o form inteiro é
 * desabilitado; quando o agente responde, o bloco é substituído pela confirmação (o parent
 * remove o form). O `action_token` (idempotência, §12.2b) é gerado uma vez por instância.
 */

export type FormValues = Record<string, string | string[]>;

/** Resultado do submit: `ok` = enviado com sucesso (não houve erro/reemissão do form). */
export interface SubmitResult {
  ok: boolean;
}

export interface FormBlockHandlers {
  onSubmit?: (payload: {
    formId: string;
    blockId: string;
    actionToken: string;
    values: FormValues;
  }) => void | Promise<SubmitResult | void>;
  onAssist?: (payload: {
    formId: string;
    blockId: string;
    action: string;
    targetField: string;
    currentValue: string;
    context: FormValues;
  }) => void | Promise<{ field: string; value: string } | void>;
}

function generateActionToken(): string {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function isEmpty(value: string | string[] | undefined): boolean {
  if (value == null) return true;
  return Array.isArray(value) ? value.length === 0 : value.trim() === "";
}

export function FormBlockView({
  block,
  handlers = {},
}: {
  block: FormBlock;
  handlers?: FormBlockHandlers;
}) {
  const { onSubmit, onAssist } = handlers;
  const { onActionClick } = useActionsTrack();
  const [values, setValues] = useState<FormValues>({});
  const [localErrors, setLocalErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [assistingField, setAssistingField] = useState<string | null>(null);
  // action_token estável por instância do form (idempotência — RFC §12.2b).
  const [actionToken] = useState(generateActionToken);

  const fields = useMemo(
    () => block.sections.map((s) => s.field),
    [block.sections],
  );

  // Erros server-side (block.errors) têm precedência sobre os locais para o campo.
  const errorFor = (name: string) => block.errors?.[name] ?? localErrors[name];

  const setValue = (name: string, value: string | string[]) => {
    setValues((prev) => ({ ...prev, [name]: value }));
    if (localErrors[name]) {
      setLocalErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const toggleChip = (field: FormField & { type: "chips" }, value: string) => {
    if (field.select === "multi") {
      const current = (values[field.name] as string[] | undefined) ?? [];
      setValue(
        field.name,
        current.includes(value)
          ? current.filter((v) => v !== value)
          : [...current, value],
      );
    } else {
      // single: clicar no já selecionado limpa (X do Figma)
      const current = values[field.name] as string | undefined;
      setValue(field.name, current === value ? "" : value);
    }
  };

  const validate = (): boolean => {
    const errors: Record<string, string> = {};
    for (const field of fields) {
      if (field.required && isEmpty(values[field.name])) {
        errors[field.name] = "Campo obrigatório";
      }
      if (
        (field.type === "text" || field.type === "textarea") &&
        field.max_length != null
      ) {
        const v = (values[field.name] as string | undefined) ?? "";
        if (v.length > field.max_length) {
          errors[field.name] = `Máximo de ${field.max_length} caracteres`;
        }
      }
    }
    setLocalErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (submitting || submitted) return;
    if (!validate()) return;

    setSubmitting(true);
    try {
      // O select de sala já carrega o operacao_id como value (backend); enviamos os values
      // como estão — o backend valida a operação e resolve o nome. Idempotência via
      // action_token torna um reenvio seguro (RFC §12.2).
      const result = await onSubmit?.({
        formId: block.form_id,
        blockId: block.id,
        actionToken,
        values,
      });
      // Sucesso (sem reemissão do form/erro) → trava o form para não reenviar. Em erro de
      // validação o backend reemite um novo form com os erros; este continua editável.
      if (result?.ok) setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  const disabled = submitting || submitted;

  const handleAssist = async (field: FormField, action: string) => {
    if (assistingField) return;
    onActionClick(
      "AGENT_ACTION_DO_FOR_ME_IMPROVE_REPORT_DESCRIPTION",
      "CHATBOT",
      {
        form_id: block.form_id,
        field: field.name,
        action,
      },
    );
    setAssistingField(field.name);
    try {
      // ST-5.1: o assist devolve um block_patch {field, value}; aplicamos ao campo alvo
      // preservando o resto do form. Falha é silenciosa e não-destrutiva (RFC §12.3).
      const patch = await onAssist?.({
        formId: block.form_id,
        blockId: block.id,
        action,
        targetField: field.name,
        currentValue: (values[field.name] as string | undefined) ?? "",
        context: values,
      });
      if (patch && patch.field) {
        setValue(patch.field, patch.value);
      }
    } finally {
      setAssistingField(null);
    }
  };

  return (
    <div className="flex w-full flex-col gap-4 rounded-xl border border-lightgray-300 bg-white p-4">
      {block.sections.map((section, index) => {
        const field = section.field;
        const error = errorFor(field.name);
        return (
          <div key={`${field.name}-${index}`} className="flex flex-col gap-2">
            <div className="flex flex-col gap-0.5">
              <span className="text-sm font-semibold text-darkgray-800">
                {section.title}
              </span>
              {section.hint && (
                <span className="text-xs text-darkgray-500">
                  {section.hint}
                </span>
              )}
            </div>

            <FieldControl
              field={field}
              value={values[field.name]}
              disabled={disabled}
              assisting={assistingField === field.name}
              onChipToggle={(v) =>
                toggleChip(field as FormField & { type: "chips" }, v)
              }
              onValueChange={(v) => setValue(field.name, v)}
              onAssist={handleAssist}
            />

            {error && <span className="text-xs text-danger-700">{error}</span>}
          </div>
        );
      })}

      <Button
        type="button"
        onClick={handleSubmit}
        disabled={disabled}
        className="mt-1"
      >
        {submitted
          ? "Enviado ✓"
          : submitting
            ? "Enviando..."
            : block.submit_label}
      </Button>
    </div>
  );
}

function FieldControl({
  field,
  value,
  disabled,
  assisting = false,
  onChipToggle,
  onValueChange,
  onAssist,
}: {
  field: FormField;
  value: string | string[] | undefined;
  disabled: boolean;
  assisting?: boolean;
  onChipToggle: (value: string) => void;
  onValueChange: (value: string) => void;
  onAssist: (field: FormField, action: string) => void;
}) {
  if (field.type === "chips") {
    const selected = Array.isArray(value)
      ? value
      : value
        ? [value as string]
        : [];
    return (
      <div className="flex flex-wrap gap-2">
        {field.options.map((opt) => {
          const isSelected = selected.includes(opt.value);
          return (
            <button
              key={opt.value}
              type="button"
              disabled={disabled}
              aria-pressed={isSelected}
              onClick={() => onChipToggle(opt.value)}
              className={cn(
                "inline-flex items-center gap-1 rounded-full border px-4 py-2 text-sm font-medium transition-colors",
                isSelected
                  ? "border-darkgray-900 bg-darkgray-900 text-white"
                  : "border-lightgray-400 bg-white text-darkgray-700 hover:bg-lightgray-100",
                disabled && "opacity-60",
              )}
            >
              {opt.label}
              {isSelected && field.select === "single" && (
                <X className="h-3 w-3" aria-hidden />
              )}
            </button>
          );
        })}
      </div>
    );
  }

  if (field.type === "select") {
    return (
      <select
        disabled={disabled}
        value={(value as string) ?? ""}
        onChange={(e) => onValueChange(e.target.value)}
        className="w-full rounded-lg border border-lightgray-400 bg-white px-3 py-2 text-sm text-darkgray-700"
      >
        <option value="" disabled>
          {field.placeholder ?? "Selecione"}
        </option>
        {field.options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    );
  }

  if (field.type === "textarea") {
    return (
      <div className="flex flex-col gap-1">
        <textarea
          disabled={disabled}
          value={(value as string) ?? ""}
          placeholder={field.placeholder}
          maxLength={field.max_length}
          onChange={(e) => onValueChange(e.target.value)}
          rows={3}
          className="w-full resize-y rounded-lg border border-lightgray-400 bg-white px-3 py-2 text-sm text-darkgray-700"
        />
        {field.assist_action && (
          <button
            type="button"
            disabled={disabled || assisting}
            onClick={() => onAssist(field, field.assist_action!.action)}
            className="inline-flex items-center gap-1 self-start text-xs font-medium text-aiBlue-700 hover:underline disabled:opacity-60"
          >
            <Sparkles
              className={cn("h-3 w-3", assisting && "animate-pulse")}
              aria-hidden
            />
            {assisting ? "Melhorando..." : field.assist_action.label}
          </button>
        )}
      </div>
    );
  }

  // text
  return (
    <input
      type="text"
      disabled={disabled}
      value={(value as string) ?? ""}
      placeholder={field.placeholder}
      maxLength={field.max_length}
      onChange={(e) => onValueChange(e.target.value)}
      className="w-full rounded-lg border border-lightgray-400 bg-white px-3 py-2 text-sm text-darkgray-700"
    />
  );
}
